import { ReactElement } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { UsageBucket, RouteBucket } from "../../../../data/clients/ExternalApiClient";
import { formatCompact, formatInt } from "../shared/formatters";

interface Props {
  series: UsageBucket[];
  topRoutes: RouteBucket[];
  scopeBreakdown: { bucket: string; calls: number }[];
  loading?: boolean;
}

export function UsageCharts({
  series,
  topRoutes,
  scopeBreakdown,
  loading,
}: Props): ReactElement {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Kald over tid
            </div>
            <div className="text-xs text-slate-500 font-mono tabular-nums">
              {formatInt(series.reduce((sum, s) => sum + s.calls, 0))} i alt
            </div>
          </div>
          <div className="h-72 px-3 py-3">
            {loading ? (
              <SkeletonChart />
            ) : series.length === 0 ? (
              <ChartEmpty />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <CartesianGrid
                    strokeDasharray="0"
                    stroke="rgb(241 245 249)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="bucket"
                    stroke="rgb(148 163 184)"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="rgb(148 163 184)"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 2,
                      border: "1px solid rgb(226 232 240)",
                      fontSize: 12,
                      padding: "6px 8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="calls"
                    stroke="rgb(15 23 42)"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 3, stroke: "rgb(15 23 42)", strokeWidth: 1.5, fill: "white" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Per scope
            </div>
          </div>
          <div className="h-72 px-3 py-3">
            {loading ? (
              <SkeletonChart />
            ) : scopeBreakdown.length === 0 ? (
              <ChartEmpty />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scopeBreakdown} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="0"
                    stroke="rgb(241 245 249)"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    stroke="rgb(148 163 184)"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="bucket"
                    stroke="rgb(148 163 184)"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    width={110}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 2,
                      border: "1px solid rgb(226 232 240)",
                      fontSize: 12,
                      padding: "6px 8px",
                    }}
                  />
                  <Bar
                    dataKey="calls"
                    fill="rgb(15 23 42)"
                    radius={[0, 2, 2, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Mest kaldte ruter
          </div>
        </div>
        {topRoutes.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-slate-400">
            Ingen trafik i dette interval.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold border-b border-slate-100">
                <th className="text-left px-5 py-2 w-20">Metode</th>
                <th className="text-left px-3 py-2">Rute</th>
                <th className="text-right px-5 py-2">Kald</th>
              </tr>
            </thead>
            <tbody>
              {topRoutes.map((r, i) => (
                <tr
                  key={i}
                  className="border-b border-slate-100 last:border-0"
                >
                  <td className="px-5 py-2">
                    <span className="px-1.5 py-0.5 text-[10px] font-mono text-slate-600 bg-slate-100 rounded-sm">
                      {r.method}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-900">
                    {r.route}
                  </td>
                  <td className="px-5 py-2 text-right font-mono text-xs text-slate-900 tabular-nums">
                    {formatInt(r.calls)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ChartEmpty(): ReactElement {
  return (
    <div className="h-full flex items-center justify-center text-xs text-slate-400">
      Ingen data i dette interval.
    </div>
  );
}

function SkeletonChart(): ReactElement {
  return (
    <div className="h-full flex items-end gap-1 px-2">
      {Array.from({ length: 24 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 bg-slate-100 rounded-sm animate-pulse"
          style={{ height: `${20 + ((i * 13) % 70)}%` }}
        />
      ))}
    </div>
  );
}
